import { api } from "../../lib/api";
import JsBarcode from "jsbarcode";
import qrcode from "qrcode-generator";

export const RD = "RD$";

// Company WhatsApp click-to-chat URL from POS settings (digits only).
export function waUrl(s = {}) {
  const num = (s.whatsapp || s.phone || "").replace(/\D/g, "");
  return num ? `https://wa.me/${num}` : "";
}

// Synchronous QR data URL (qrcode-generator) for embedding in printed docs.
export function qrDataUrl(text, cellSize = 4) {
  if (!text) return "";
  try {
    const qr = qrcode(0, "M");
    qr.addData(text);
    qr.make();
    return qr.createDataURL(cellSize, 0);
  } catch (e) {
    return "";
  }
}

export function rd(amount) {
  const n = Number(amount || 0);
  return `RD$ ${n.toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export const inputCls =
  "w-full bg-white border border-neutral-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-rose focus:ring-1 focus:ring-rose/30 transition";

export const labelCls = "text-[11px] uppercase tracking-[0.12em] text-neutral-500";

export const CATEGORIES = [
  { key: "miniperfume", label: "Miniperfume" },
  { key: "perfume", label: "Perfume" },
  { key: "set", label: "Set" },
  { key: "accesorio", label: "Accesorio" },
];

export const GENDERS = [
  { key: "mujer", label: "Mujer" },
  { key: "hombre", label: "Hombre" },
  { key: "unisex", label: "Unisex" },
];

export const ADJUST_REASONS = [
  { key: "compra", label: "Compra de mercancía", sign: 1 },
  { key: "devolucion", label: "Devolución", sign: 1 },
  { key: "correccion", label: "Corrección de inventario", sign: 0 },
  { key: "danado", label: "Producto dañado", sign: -1 },
  { key: "perdido", label: "Producto perdido", sign: -1 },
  { key: "muestra", label: "Muestra / probador", sign: -1 },
];

export const NCF_TYPES = [
  { key: "consumo", label: "Factura de consumo" },
  { key: "credito_fiscal", label: "Crédito fiscal" },
  { key: "nota_credito", label: "Nota de crédito" },
  { key: "otro", label: "Otro" },
];

export const PAYMENT_METHODS = [
  { key: "efectivo", label: "Efectivo" },
  { key: "tarjeta", label: "Tarjeta" },
  { key: "transferencia", label: "Transferencia" },
  { key: "link_pago", label: "Link de Pago" },
  { key: "credito", label: "Crédito" },
  { key: "combinado", label: "Pago combinado" },
];

export function paymentLabel(key) {
  const m = PAYMENT_METHODS.find((x) => x.key === key);
  return m ? m.label : (key || "");
}

export async function askPin(message) {
  const pin = window.prompt(message || "Ingresa el PIN de administrador para continuar:");
  return pin || "";
}

let _audioCtx;
// Short beep for scanner feedback: ok = high confirmation tone, error = low double tone.
export function beep(ok = true) {
  try {
    _audioCtx = _audioCtx || new (window.AudioContext || window.webkitAudioContext)();
    const ctx = _audioCtx;
    if (ctx.state === "suspended") ctx.resume();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.connect(g);
    g.connect(ctx.destination);
    o.type = "square";
    const now = ctx.currentTime;
    if (ok) {
      o.frequency.setValueAtTime(1046, now);
    } else {
      o.frequency.setValueAtTime(300, now);
      o.frequency.setValueAtTime(180, now + 0.14);
    }
    g.gain.setValueAtTime(0.09, now);
    g.gain.exponentialRampToValueAtTime(0.0001, now + (ok ? 0.16 : 0.32));
    o.start(now);
    o.stop(now + (ok ? 0.17 : 0.33));
  } catch (e) { console.debug("Audio no disponible para el beep:", e); }
}

const esc = (v) =>
  String(v ?? "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));

function invoiceLines(inv) {
  return inv.items
    .map(
      (i) =>
        `<tr><td class="l">${esc(i.name)}</td><td class="c">${i.qty}</td><td class="r">${rd(i.price)}</td><td class="r">${rd(i.price * i.qty)}</td></tr>`
    )
    .join("");
}

function totalsBlock(inv) {
  const isQuote = inv.doc_type === "cotizacion";
  let h = `<div class="row strong"><span>Subtotal</span><span>${rd(inv.subtotal)}</span></div>`;
  if (inv.discount > 0) h += `<div class="row"><span>Descuento</span><span>- ${rd(inv.discount)}</span></div>`;
  if (inv.itbis > 0) h += `<div class="row"><span>ITBIS ${inv.itbis_rate}%</span><span>${rd(inv.itbis)}</span></div>`;
  if (inv.delivery_fee > 0) h += `<div class="row"><span>Delivery${inv.delivery_label ? ` (${esc(inv.delivery_label)})` : ""}</span><span>${rd(inv.delivery_fee)}</span></div>`;
  h += `<div class="row total"><span>TOTAL</span><span>${rd(inv.total)}</span></div>`;
  if (!isQuote) {
    h += `<div class="row"><span>Pago</span><span>${esc(paymentLabel(inv.payment_method))}</span></div>`;
    if (inv.change > 0) h += `<div class="row strong"><span>Cambio</span><span>${rd(inv.change)}</span></div>`;
    if (inv.balance > 0) h += `<div class="row strong"><span>Balance</span><span>${rd(inv.balance)}</span></div>`;
  }
  return h;
}

const docLabel = (inv) => (inv && inv.doc_type === "cotizacion" ? "Cotización" : "Factura");

function openPrint(html, features = "width=420,height=640") {
  // Avoid document.write: serve generated HTML via a same-origin Blob URL.
  const blob = new Blob([html], { type: "text/html" });
  const url = URL.createObjectURL(blob);
  const w = window.open(url, "_blank", features);
  if (!w) { URL.revokeObjectURL(url); return; }
  let printed = false;
  const doPrint = () => { if (printed) return; printed = true; try { w.focus(); w.print(); } catch (e) { /* print blocked */ } };
  w.addEventListener("load", () => setTimeout(doPrint, 250));
  setTimeout(doPrint, 1200);
  setTimeout(() => { try { URL.revokeObjectURL(url); } catch (e) { /* noop */ } }, 60000);
}

export function printDoc(html) {
  openPrint(html);
}

export function printThermal(inv, s = {}) {
  const head = `
    <div class="center">
      ${s.logo ? `<img src="${esc(s.logo)}" class="logo"/>` : ""}
      <div class="biz">${esc(s.business_name || "MO by Vir")}</div>
      ${s.rnc ? `<div class="sm">RNC: ${esc(s.rnc)}</div>` : ""}
      ${s.address ? `<div class="sm">${esc(s.address)}</div>` : ""}
      ${s.phone ? `<div class="sm">Tel: ${esc(s.phone)}</div>` : ""}
    </div>
    <div class="hr"></div>
    <div class="sm"><b>${docLabel(inv)}:</b> ${esc(inv.number)}</div>
    ${inv.ncf ? `<div class="sm"><b>NCF:</b> ${esc(inv.ncf)}</div>` : ""}
    <div class="sm"><b>Fecha:</b> ${(inv.created_at || "").slice(0, 16).replace("T", " ")}</div>
    ${inv.valid_until ? `<div class="sm"><b>Válida hasta:</b> ${esc(inv.valid_until)}</div>` : ""}
    <div class="sm"><b>Cliente:</b> ${esc(inv.customer_name)}</div>
    ${inv.customer_address ? `<div class="sm"><b>Dirección:</b> ${esc(inv.customer_address)}</div>` : ""}
    <div class="hr"></div>`;
  const html = `<!doctype html><html><head><meta charset="utf-8"><title>${esc(inv.number)}</title>
    <style>
      @page { size: 80mm auto; margin: 2mm; }
      * { box-sizing: border-box; }
      body { width: 76mm; margin: 0; font-family: 'Courier New', monospace; color:#000; font-size: 12px; line-height: 1.35; }
      .center { text-align:center; }
      .logo { max-height: 40px; margin: 0 auto 4px; display:block; }
      .biz { font-size: 16px; font-weight: bold; }
      .sm { font-size: 11px; margin: 1px 0; }
      .hr { border-top: 1px dashed #000; margin: 6px 0; }
      table { width:100%; border-collapse: collapse; font-size: 11px; }
      th { text-align:left; border-bottom:1px solid #000; padding: 2px 0; font-weight:bold; }
      td { padding: 2px 0; }
      .c { text-align:center; } .r { text-align:right; } .l { text-align:left; }
      .row { display:flex; justify-content:space-between; font-size: 12px; margin: 2px 0; }
      .row.strong { font-weight:bold; font-size: 13px; }
      .total { font-weight:bold; font-size: 16px; border-top:1px solid #000; padding-top:4px; margin-top:4px; }
      .foot { text-align:center; font-size:11px; margin-top:8px; }
      .anul { text-align:center; color:#b00; font-weight:bold; margin-top:6px; }
    </style></head><body>
    ${head}
    ${inv._provisional ? `<div class="anul">*** PENDIENTE DE SINCRONIZAR ***</div><div class="sm center">Comprobante provisional</div><div class="hr"></div>` : ""}
    <table><thead><tr><th>Producto</th><th class="c">Cant</th><th class="r">Precio</th><th class="r">Total</th></tr></thead>
    <tbody>${invoiceLines(inv)}</tbody></table>
    <div class="hr"></div>
    ${totalsBlock(inv)}
    <div class="foot">Vendedor: ${esc(inv.seller || "")}</div>
    <div class="foot">${esc(s.thank_you_message || "¡Gracias por su compra!")}</div>
    ${s.return_policy ? `<div class="foot">${esc(s.return_policy)}</div>` : ""}
    ${inv.status === "anulada" ? `<div class="anul">*** ANULADA ***</div>` : ""}
    </body></html>`;
  openPrint(html);
}

export function printA4(inv, s = {}) {
  const html = `<!doctype html><html><head><meta charset="utf-8"><title>${esc(inv.number)}</title>
    <style>
      @page { size: A4; margin: 16mm; }
      body { font-family: 'Helvetica Neue', Arial, sans-serif; color:#2C2621; font-size: 14px; line-height: 1.5; }
      .head { display:flex; justify-content:space-between; align-items:flex-start; border-bottom:2px solid #B76E79; padding-bottom:16px; }
      .logo { max-height: 64px; }
      .biz { font-size: 26px; font-weight:600; color:#B76E79; margin:0; }
      .sm { font-size: 13px; color:#555; margin:2px 0; }
      .meta { text-align:right; }
      .title { font-size: 20px; letter-spacing:2px; text-transform:uppercase; color:#B76E79; margin:0 0 6px; }
      table { width:100%; border-collapse: collapse; margin-top: 24px; }
      th { background:#F5F2EB; text-align:left; padding: 10px; font-size: 12px; text-transform:uppercase; letter-spacing:1px; color:#2C2621; }
      td { padding: 10px; border-bottom:1px solid #eee; font-size: 14px; }
      .c { text-align:center; } .r { text-align:right; }
      .totals { width: 320px; margin-left:auto; margin-top: 16px; }
      .row { display:flex; justify-content:space-between; padding: 5px 0; font-size: 14px; }
      .row.strong { font-weight:700; font-size: 15px; }
      .total { font-size: 20px; font-weight:700; border-top:2px solid #2C2621; margin-top:6px; padding-top:8px; }
      .foot { text-align:center; margin-top: 40px; color:#B76E79; }
      .wa-cta { text-align:center; margin-top: 28px; padding-top:18px; border-top:1px dashed #ddd; }
      .wa-cta img { width:120px; height:120px; }
      .wa-txt { font-size:13px; color:#2C2621; margin-top:6px; font-weight:600; }
      .wa-link { font-size:12px; color:#25D366; text-decoration:none; }
      .policy { font-size: 11px; color:#999; text-align:center; margin-top:6px; }
      .anul { text-align:center; color:#b00; font-weight:bold; font-size:22px; margin:20px 0; letter-spacing:4px; }
    </style></head><body>
    <div class="head">
      <div>
        ${s.logo ? `<img src="${esc(s.logo)}" class="logo"/>` : `<h1 class="biz">${esc(s.business_name || "MO by Vir")}</h1>`}
        ${s.logo ? `<h1 class="biz" style="margin-top:6px">${esc(s.business_name || "MO by Vir")}</h1>` : ""}
        ${s.rnc ? `<div class="sm">RNC: ${esc(s.rnc)}</div>` : ""}
        ${s.address ? `<div class="sm">${esc(s.address)}</div>` : ""}
        ${s.phone ? `<div class="sm">Tel: ${esc(s.phone)}</div>` : ""}
        ${s.email ? `<div class="sm">${esc(s.email)}</div>` : ""}
      </div>
      <div class="meta">
        <p class="title">${docLabel(inv)}</p>
        <div class="sm"><b>No.</b> ${esc(inv.number)}</div>
        ${inv.ncf ? `<div class="sm"><b>NCF:</b> ${esc(inv.ncf)}</div>` : ""}
        <div class="sm"><b>Fecha:</b> ${(inv.created_at || "").slice(0, 16).replace("T", " ")}</div>
        ${inv.valid_until ? `<div class="sm"><b>Válida hasta:</b> ${esc(inv.valid_until)}</div>` : ""}
        <div class="sm"><b>Cliente:</b> ${esc(inv.customer_name)}</div>
        ${inv.customer_address ? `<div class="sm"><b>Dirección:</b> ${esc(inv.customer_address)}</div>` : ""}
      </div>
    </div>
    ${inv.status === "anulada" ? `<div class="anul">ANULADA</div>` : ""}
    <table><thead><tr><th>Producto</th><th class="c">Cant</th><th class="r">Precio</th><th class="r">Total</th></tr></thead>
    <tbody>${invoiceLines(inv)}</tbody></table>
    <div class="totals">${totalsBlock(inv)}</div>
    ${waUrl(s) ? `<div class="wa-cta">
      <img src="${qrDataUrl(waUrl(s))}" alt="WhatsApp QR" />
      <div class="wa-txt">Escríbenos por WhatsApp</div>
      <a href="${esc(waUrl(s))}" class="wa-link">${esc((s.whatsapp || s.phone || ""))}</a>
    </div>` : ""}
    <div class="foot">${esc(s.thank_you_message || "¡Gracias por su compra!")}</div>
    ${s.return_policy ? `<div class="policy">${esc(s.return_policy)}</div>` : ""}
    <div class="policy">Vendedor: ${esc(inv.seller || "")}</div>
    </body></html>`;
  openPrint(html);
}

export function invoiceWhatsAppUrl(invoice, s = {}, toNumber = "") {
  const isQuote = invoice.doc_type === "cotizacion";
  let m = `*${s.business_name || "MO by Vir"}*\n`;
  m += `${isQuote ? "Cotización" : "Factura"} ${invoice.number}${invoice.ncf ? ` · NCF ${invoice.ncf}` : ""}\n`;
  m += `Cliente: ${invoice.customer_name}\n\n`;
  invoice.items.forEach((i) => { m += `${i.qty} × ${i.name} — ${rd(i.price * i.qty)}\n`; });
  m += `\nSubtotal: ${rd(invoice.subtotal)}\n`;
  if (invoice.discount) m += `Descuento: -${rd(invoice.discount)}\n`;
  if (invoice.itbis) m += `ITBIS: ${rd(invoice.itbis)}\n`;
  m += `*Total: ${rd(invoice.total)}*\n`;
  if (!isQuote) m += `Pago: ${invoice.payment_method}\n`;
  if (isQuote && invoice.valid_until) m += `Válida hasta: ${invoice.valid_until}\n`;
  m += `\n${s.thank_you_message || "¡Gracias por su compra!"}`;
  const num = (toNumber || s.whatsapp || "").replace(/\D/g, "");
  return `https://wa.me/${num}?text=${encodeURIComponent(m)}`;
}

export function reminderWhatsAppUrl(invoice, s = {}, toNumber = "") {
  const name = (invoice.customer_name || "").split(" ")[0] || "";
  let m = `Hola ${name}, le saluda *${s.business_name || "MO by Vir"}* 🌸\n\n`;
  m += `Le recordamos amablemente que tiene un balance pendiente de *${rd(invoice.balance)}* correspondiente a la factura ${invoice.number}`;
  if (invoice.credit_due_date) m += ` (fecha acordada: ${invoice.credit_due_date})`;
  m += `.\n\n¿Podríamos coordinar el pago? ¡Muchas gracias! 💕`;
  const num = (toNumber || "").replace(/\D/g, "");
  return `https://wa.me/${num}?text=${encodeURIComponent(m)}`;
}

export function printCashClose(d, s = {}) {
  const methodRows = (d.by_payment_method || [])
    .map((m) => `<tr><td class="cap">${esc(paymentLabel(m.method))}</td><td class="c">${m.count}</td><td class="r">${rd(m.total)}</td></tr>`)
    .join("") || `<tr><td colspan="3" class="c">Sin ventas</td></tr>`;
  const line = (l, v) => `<div class="row"><span>${l}</span><span>${v}</span></div>`;
  const logoTag = s.logo ? `<img src="${esc(s.logo)}" class="logo"/>` : "";
  const rango = `${esc(d.first_invoice || "—")} a ${esc(d.last_invoice || "—")}`;
  const closedAt = (d.generated_at || "").slice(0, 16).replace("T", " ");
  const body =
    line("Facturas", d.invoice_count) +
    line("Unidades vendidas", d.units_sold) +
    line("Rango", rango) +
    (d.offline_synced_count ? line("Offline sincronizadas", d.offline_synced_count) : "") +
    line("Anuladas", d.voided_count);
  const totals =
    line("Descuentos", rd(d.total_discount)) +
    line("ITBIS cobrado", rd(d.total_itbis)) +
    (d.delivery_total ? line("Cobrado delivery", rd(d.delivery_total)) : "") +
    line("Efectivo en caja", rd(d.cash_received)) +
    line("Pendiente de cobro", rd(d.pending_balance)) +
    (d.estimated_profit != null ? line("Ganancia estimada", rd(d.estimated_profit)) : "");
  const html = `<!doctype html><html><head><meta charset="utf-8"><title>Cierre ${esc(d.date)}</title>
    <style>
      @page { size: 80mm auto; margin: 3mm; }
      body { width: 74mm; margin:0; font-family:'Courier New',monospace; color:#000; font-size:11px; }
      .center { text-align:center; }
      .logo { max-height: 40px; margin:0 auto 4px; display:block; }
      .biz { font-size:14px; font-weight:bold; }
      .ttl { font-size:13px; font-weight:bold; margin:6px 0; letter-spacing:2px; }
      .sm { font-size:10px; }
      .hr { border-top:1px dashed #000; margin:6px 0; }
      table { width:100%; border-collapse:collapse; font-size:10px; }
      th { text-align:left; border-bottom:1px solid #000; padding:2px 0; }
      td { padding:2px 0; } .c{text-align:center;} .r{text-align:right;} .cap{text-transform:capitalize;}
      .row { display:flex; justify-content:space-between; margin:1px 0; }
      .big { font-weight:bold; font-size:13px; border-top:1px solid #000; padding-top:3px; margin-top:3px; }
    </style></head><body>
    <div class="center">
      ${logoTag}
      <div class="biz">${esc(s.business_name || "MO by Vir")}</div>
      <div class="ttl">CIERRE DE CAJA</div>
      <div class="sm">${esc(d.date)}</div>
    </div>
    <div class="hr"></div>
    ${body}
    <div class="hr"></div>
    <table><thead><tr><th>Método</th><th class="c">#</th><th class="r">Total</th></tr></thead>
    <tbody>${methodRows}</tbody></table>
    <div class="hr"></div>
    ${totals}
    <div class="row big"><span>VENTA TOTAL</span><span>${rd(d.total_sales)}</span></div>
    <div class="hr"></div>
    <div class="sm center">Cerrado: ${closedAt}</div>
    <div class="sm center">${esc(s.thank_you_message || "")}</div>
    </body></html>`;
  openPrint(html);
}

export function printProductCatalog(products = [], s = {}) {
  const cat = (k) => (CATEGORIES.find((c) => c.key === k) || {}).label || k || "";
  const rows = products
    .map(
      (p, i) =>
        `<tr>
          <td class="c">${i + 1}</td>
          <td class="l"><b>${esc(p.name)}</b>${p.code ? `<div class="muted">${esc(p.code)}</div>` : ""}</td>
          <td class="l muted">${esc(p.description || "—")}</td>
          <td class="l">${esc(cat(p.category))}</td>
          <td class="r">${rd(p.cost)}</td>
          <td class="r"><b>${rd(p.price)}</b></td>
          <td class="c">${p.qty ?? 0}</td>
        </tr>`
    )
    .join("") || `<tr><td colspan="7" class="c">Sin productos</td></tr>`;
  const totalCost = products.reduce((a, p) => a + (Number(p.cost) || 0) * (Number(p.qty) || 0), 0);
  const totalRetail = products.reduce((a, p) => a + (Number(p.price) || 0) * (Number(p.qty) || 0), 0);
  const now = new Date().toLocaleString("es-DO", { dateStyle: "long", timeStyle: "short" });
  const html = `<!doctype html><html><head><meta charset="utf-8"><title>Lista de productos — ${esc(s.business_name || "MO by Vir")}</title>
    <style>
      @page { size: A4; margin: 14mm; }
      body { font-family: 'Helvetica Neue', Arial, sans-serif; color:#2C2621; font-size: 12px; }
      .head { display:flex; justify-content:space-between; align-items:flex-start; border-bottom:2px solid #B76E79; padding-bottom:12px; margin-bottom:16px; }
      .logo { max-height: 52px; }
      .biz { font-size: 22px; font-weight:600; color:#B76E79; margin:0; }
      .sm { font-size: 12px; color:#555; margin:2px 0; }
      .title { font-size: 16px; letter-spacing:2px; text-transform:uppercase; color:#B76E79; margin:0 0 4px; text-align:right; }
      table { width:100%; border-collapse: collapse; }
      th { background:#F5F2EB; text-align:left; padding: 8px; font-size: 11px; text-transform:uppercase; letter-spacing:.5px; border-bottom:1px solid #e5e0d5; }
      td { padding: 7px 8px; border-bottom:1px solid #f0ece3; vertical-align:top; }
      .c { text-align:center; } .r { text-align:right; } .l { text-align:left; }
      .muted { color:#8a8a8a; font-size: 11px; }
      tfoot td { font-weight:700; background:#faf8f3; border-top:2px solid #2C2621; }
      .count { color:#555; margin: 4px 0 12px; }
    </style></head><body>
    <div class="head">
      <div>
        ${s.logo ? `<img src="${esc(s.logo)}" class="logo"/>` : ""}
        <h1 class="biz">${esc(s.business_name || "MO by Vir")}</h1>
        ${s.rnc ? `<div class="sm">RNC: ${esc(s.rnc)}</div>` : ""}
      </div>
      <div>
        <p class="title">Lista de productos</p>
        <div class="sm">${esc(now)}</div>
      </div>
    </div>
    <div class="count">Total de artículos: <b>${products.length}</b></div>
    <table>
      <thead><tr>
        <th class="c">#</th><th>Producto</th><th>Descripción</th><th>Categoría</th>
        <th class="r">P. compra</th><th class="r">P. venta</th><th class="c">Stock</th>
      </tr></thead>
      <tbody>${rows}</tbody>
      <tfoot><tr>
        <td colspan="4" class="r">Valor del inventario</td>
        <td class="r">${rd(totalCost)}</td>
        <td class="r">${rd(totalRetail)}</td>
        <td></td>
      </tr></tfoot>
    </table>
    </body></html>`;
  openPrint(html, "width=900,height=700");
}

export function printBarcodeLabels(product, copies = 1) {
  const value = product.barcode || product.code || product.id;
  const labels = Array.from({ length: Math.max(1, Number(copies) || 1) }, () => {
    let svg = "";
    const el = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    try {
      JsBarcode(el, String(value), { format: "CODE128", width: 1.6, height: 40, fontSize: 12, margin: 4 });
      svg = el.outerHTML;
    } catch (e) {
      svg = `<div class="code">${esc(value)}</div>`;
    }
    return `<div class="label"><div class="name">${esc(product.name)}</div>${svg}<div class="price">${rd(product.price)}</div></div>`;
  }).join("");
  const html = `<!doctype html><html><head><meta charset="utf-8"><title>Etiquetas</title>
    <style>
      @page { size: 50mm 30mm; margin: 1mm; }
      body { margin:0; font-family: Arial, sans-serif; }
      .label { width: 48mm; height: 28mm; page-break-after: always; text-align:center; padding: 1mm; box-sizing:border-box; }
      .name { font-size: 9px; font-weight:bold; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
      .price { font-size: 13px; font-weight:bold; }
      .code { font-family: monospace; font-size: 12px; }
      svg { max-width: 46mm; height: auto; }
    </style></head><body>${labels}</body></html>`;
  openPrint(html);
}

export { api };
